import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useApi } from './lib/useApi'
import { LoadingBlock, ErrorBlock } from './components/Loading'
import { SessionProvider, useSession, isAdmin } from './lib/session'
import AppShell from './components/AppShell'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Leads from './pages/Leads'
import LeadPage from './pages/LeadPage'
import Categories from './pages/Categories'
import Targets from './pages/Targets'
import Activity from './pages/Activity'
import Billing, { StatementPage } from './pages/Billing'
import MyLeads, { MyLeadDetail } from './pages/MyLeads'
import MyClients from './pages/MyClients'
import Intake from './pages/Intake'
import Mailbox from './pages/Mailbox'
import FormatCheck from './pages/FormatCheck'
import Groups from './pages/Groups'
import Subscribers from './pages/Subscribers'
import ClientPage from './pages/ClientPage'
import People from './pages/People'
import Settings from './pages/Settings'
import FlowPage from './pages/Flow'
import Costs from './pages/Costs'
import Economics from './pages/Economics'
import SetPassword from './pages/SetPassword'
import ChooseOwnPassword from './pages/ChooseOwnPassword'
import ClientResources from './pages/client/ClientResources'
import ClientPricing from './pages/client/ClientPricing'
import Zones from './pages/Zones'
import ClientBilling, { ClientInvoice } from './pages/client/ClientBilling'
import ClientShell from './components/ClientShell'
import ClientOverview from './pages/client/ClientOverview'
import ClientLeads from './pages/client/ClientLeads'
import ClientLead from './pages/client/ClientLead'
import ClientStatement from './pages/client/ClientStatement'
import ClientSpending from './pages/client/ClientSpending'
import ClientDispute from './pages/client/ClientDispute'
import ClientDisputes from './pages/client/ClientDisputes'
import ClientProfile from './pages/client/ClientProfile'
import ClientPaymentSetup from './pages/client/ClientPaymentSetup'
import ClientSetup from './pages/client/Trial'
import ClientSettings from './pages/client/ClientSettings'
import Onboarding, { type OnboardingState } from './pages/client/Onboarding'
import Signup from './pages/client/Signup'
import ClientAgreement from './pages/client/ClientAgreement'
import JoinStart, { ConfirmEmail } from './pages/join/JoinStart'
import Applications from './pages/Applications'
import DoNotCall from './pages/DoNotCall'
import SavedReplies from './pages/SavedReplies'
import DataSafety from './pages/DataSafety'

/**
 * A client company's login: its own portal, nothing else. The server enforces
 * the same fence. Until the company has finished setup, every portal address
 * leads to the welcome flow.
 */
function ClientPortal() {
  const ob = useApi<OnboardingState>('/api/client/onboarding')
  const nav = useNavigate()
  if (ob.error) return <ErrorBlock error={ob.error} />
  if (!ob.data) return <LoadingBlock />
  // Signed itself up and not approved yet: the sign-up, and its signed Terms, nothing else.
  if ((ob.data.step as string) === 'signup') {
    return (
      <Routes>
        <Route path="portal/agreement" element={<div className="join-shell"><ClientAgreement /></div>} />
        <Route path="*" element={<Signup />} />
      </Routes>
    )
  }
  if (!ob.data.done) {
    return (
      <Routes>
        <Route path="portal/welcome" element={<Onboarding state={ob.data} onFinished={() => { ob.reload(); nav('/portal', { replace: true }) }} />} />
        <Route path="*" element={<Navigate to="/portal/welcome" replace />} />
      </Routes>
    )
  }
  return (
    <Routes>
      <Route element={<ClientShell />}>
        <Route path="portal" element={<ClientOverview />} />
        <Route path="portal/leads" element={<ClientLeads />} />
        <Route path="portal/leads/:id" element={<ClientLead />} />
        <Route path="portal/leads/:id/dispute" element={<ClientDispute />} />
        <Route path="portal/disputes" element={<ClientDisputes />} />
        <Route path="portal/statement" element={<ClientStatement />} />
        <Route path="portal/spending" element={<ClientSpending />} />
        <Route path="portal/settings" element={<ClientSettings />} />
        <Route path="portal/profile" element={<ClientProfile />} />
        <Route path="portal/resources" element={<ClientResources />} />
        <Route path="portal/pricing" element={<ClientPricing />} />
        <Route path="portal/agreement" element={<ClientAgreement />} />
        <Route path="portal/billing" element={<ClientBilling />} />
        <Route path="portal/billing/setup" element={<ClientPaymentSetup />} />
        <Route path="portal/setup" element={<ClientSetup />} />
        <Route path="portal/invoice/:period" element={<ClientInvoice />} />
        <Route path="portal/welcome" element={<Navigate to="/portal" replace />} />
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Route>
    </Routes>
  )
}

function SignedIn() {
  const { me } = useSession()
  if (me?.user.role === 'buyer') return <ClientPortal />
  const admin = isAdmin(me)
  const home = admin ? '/dashboard' : '/my'
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to={home} replace />} />
        <Route path="my" element={<MyLeads />} />
        <Route path="my/clients" element={<MyClients />} />
        {/* Reps run targets too, on the same page (Handpicked operates this, not SPEC). */}
        <Route path="my/targets" element={<Targets />} />
        <Route path="my/:id" element={<MyLeadDetail />} />
        <Route path="settings" element={<Settings />} />
        {/* Everyone who works leads sees the flow: admins and reps alike (2026-10-13). */}
        <Route path="flow" element={<FlowPage />} />
        {admin && <>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="leads" element={<Leads />} />
          <Route path="leads/:id" element={<LeadPage />} />
          <Route path="categories" element={<Categories />} />
          <Route path="targets" element={<Targets />} />
          <Route path="activity" element={<Activity />} />
          <Route path="billing" element={<Billing />} />
          <Route path="billing/:buyer/:period" element={<StatementPage />} />
          <Route path="intake" element={<Intake />} />
          <Route path="mailbox" element={<Mailbox />} />
          <Route path="format-check" element={<FormatCheck />} />
          <Route path="groups" element={<Groups />} />
          <Route path="zones" element={<Zones />} />
          <Route path="clients" element={<Subscribers />} />
          <Route path="clients/:id" element={<ClientPage />} />
          <Route path="applications" element={<Applications />} />
          <Route path="dnc" element={<DoNotCall />} />
          <Route path="replies" element={<SavedReplies />} />
          <Route path="data-safety" element={<DataSafety />} />
          <Route path="subscribers" element={<Navigate to="/clients" replace />} />
          <Route path="people" element={<People />} />
          <Route path="pricing" element={<Navigate to="/settings?tab=pricing" replace />} />
          <Route path="balances" element={<Navigate to="/clients" replace />} />
          <Route path="balances/:id" element={<ToClient />} />
          <Route path="disputes" element={<Navigate to="/clients" replace />} />
          <Route path="resources" element={<Navigate to="/settings?tab=resources" replace />} />
        </>}
        {me?.user.role === 'spec' && <>
          <Route path="economics" element={<Economics />} />
          <Route path="costs" element={<Costs />} />
        </>}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  )
}

function Gate() {
  const { status, me } = useSession()
  if (status === 'checking') return <div aria-busy="true" />
  if (status === 'signed_out') return <Login />
  // Signed in on a starting password someone else chose: choose your own first.
  if (me?.must_change_password) return <ChooseOwnPassword />
  return <SignedIn />
}

export default function App() {
  const { pathname } = useLocation()
  // The old no-login private page was retired for the portal: its links land on sign-in.
  if (pathname.startsWith('/r/')) return <Navigate to="/portal/leads" replace />
  // Sign-up and the email-confirm link work signed out.
  if (pathname === '/join' || pathname.startsWith('/join/') || pathname.startsWith('/confirm/')) {
    return <Routes><Route path="/join" element={<JoinStart />} /><Route path="/join/:slug" element={<JoinStart />} /><Route path="/confirm/:token" element={<ConfirmEmail />} /></Routes>
  }
  // Invite and reset links work signed out, before a password exists.
  if (pathname.startsWith('/welcome/') || pathname.startsWith('/reset/')) {
    return <Routes><Route path="/welcome/:token" element={<SetPassword />} /><Route path="/reset/:token" element={<SetPassword />} /></Routes>
  }
  return (
    <SessionProvider>
      <Gate />
    </SessionProvider>
  )
}

/** Old /balances/:id links land on the client's page. */
function ToClient() {
  const { id } = useParams()
  return <Navigate to={`/clients/${id}`} replace />
}
